import http from '@/api/http'
import { performersPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

interface PerformerRole {
  // Role assigned to a performer row from the persons API.

  id: number
  // Role id.

  name: string
  // Role name (e.g. `performer`).

  description: string | null
  // Optional role description.
}

export interface Performer {
  // One performer record from GET `/persons/performers`.
  // Mirrors the persons-API shape used by `Client`, but performers do not have
  // a balance / visit history / business service count populated in this view,
  // so those fields are typed as nullable / always-empty placeholders.

  id: string
  // Performer id (matches `performers_ids` on a `BusinessService`).

  user_id: number | null
  // Linked user id when present.

  name: string
  // Display name (typically `Last First`, e.g. `Поланевич Алина`).

  birthday: string | null
  // Birth date string when present.

  roles: PerformerRole[]
  // Assigned roles (always includes `performer`).

  phone: string | null
  // Phone when present.

  email: string | null
  // Email when present.

  gender: string
  // Sex/gender string from the API.

  bills: unknown[]
  // Bills linked to the performer; shape from backend.

  diseases: unknown[]
  // Diseases linked to the performer; shape from backend.

  social_network_accounts: unknown[]
  // Social accounts; shape from backend.

  payments: unknown[]
  // Payments; shape from backend.

  start_datetime: string | null
  // Service start datetime when the API includes it.

  last_visit_datetime: string | null
  // Last visit datetime when the API includes it (often null for performers).

  current_balance: unknown | null
  // Balance snapshot; null for performers in this endpoint.

  business_service_count: number | null
  // Number of business services; null for performers in this endpoint.
}

interface PerformersResponse extends PageMeta {
  // Paginated list payload from GET `/persons/performers`.

  items: Performer[]
  // Page of performers.
}

/**
 * Fetches a page of performers. Same cursor/limit contract as `fetchClients`.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchPerformers(cursor?: number): Promise<PerformersResponse> {
  const { data } = await http.get<PerformersResponse>('/persons/performers', {
    params: cursorParams(performersPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchPerformers] GET /persons/performers response', data)
  }
  return data
}
